import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import type { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";
import { VEHICLE_BUY_IN_RESOURCE } from "./use-vehicle-buy-in-create-data.query";

export const VEHICLE_BUY_IN_SEARCH_API_PATH = "/v1/vehicle-buy-in/search" as const;

export type VehicleBuyInSearchParameters = paths[typeof VEHICLE_BUY_IN_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type VehicleBuyInSearchBody =
  paths[typeof VEHICLE_BUY_IN_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type VehicleBuyInSearchResponse =
  paths[typeof VEHICLE_BUY_IN_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useVehicleBuyInSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<VehicleBuyInSearchParameters, VehicleBuyInSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    VehicleBuyInSearchResponse,
    Error,
    VehicleBuyInSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const resolvedOptions =
    optionsWithDefaultsPaginated<PaginatedOptions<VehicleBuyInSearchParameters, VehicleBuyInSearchBody>>(options);
  const parameters = handlePaginatedParameters(resolvedOptions.parameters);
  const body = resolvedOptions.body ?? {};

  return useQuery({
    queryKey: [VEHICLE_BUY_IN_RESOURCE, "search", { parameters, body }],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<VehicleBuyInSearchResponse>(VEHICLE_BUY_IN_SEARCH_API_PATH, body, {
        params: parameters,
      });
      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
